import React, { useEffect, useMemo } from 'react';

import { useAppContext } from '../../../../../common/ui/app-context';
import LeapQueryComponent from '../../../../../common/ui/leap-query';
import { onLeapQueryCandidateClicked } from '../../../../../common/utils/events/leap-query-candidate-clicked';
import { onLeapQueryShown } from '../../../../../common/utils/events/leap-query-shown';
import { useAnalytics, useCommonAndPrivateAttributes } from '../../../../../controllers/analytics';
import { ErrorBoundary } from '../../../../../controllers/error-boundary';
import { useLeapQuery } from '../../../../../controllers/results';
import { useQueryInfo } from '../../../../../controllers/store/search';

const LeapQuery = (): React.JSX.Element => {
	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useCommonAndPrivateAttributes();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const [queryInfo] = useQueryInfo();
	const { loading, candidates, titleIndex } = useLeapQuery();
	const { generateSearchUrl, queryParams } = useAppContext();

	const { originalQuery } = queryInfo || {};

	const mappedCandidates = useMemo(
		() =>
			candidates.slice(0, 4).map((candidate) => ({
				label: candidate,
				href: generateSearchUrl({
					...queryParams,
					text: candidate,
					originalQuery,
				}),
			})),
		[candidates, generateSearchUrl, originalQuery, queryParams],
	);

	const handleCandidateClicked = (candidateIndex: number) => {
		fireAnalyticsEvent(
			onLeapQueryCandidateClicked({
				candidateIndex,
				titleIndex,
				commonAttributes,
				nonPrivacySafeAttributes,
			}),
		);
	};

	useEffect(() => {
		if (loading || candidates.length === 0) {
			return;
		}

		fireAnalyticsEvent(
			onLeapQueryShown({
				candidatesCount: candidates.length,
				titleIndex,
				commonAttributes,
				nonPrivacySafeAttributes,
			}),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [candidates.length, loading]);

	return (
		<LeapQueryComponent
			candidates={mappedCandidates}
			loading={loading}
			titleIndex={titleIndex}
			handleCandidateClicked={handleCandidateClicked}
		/>
	);
};

export const LeapQueryWithErrorBoundary = (): React.JSX.Element => (
	<ErrorBoundary section="SearchPageLeapQuery">
		<LeapQuery />
	</ErrorBoundary>
);

export default LeapQuery;
